import { supabaseCollectionOptions } from '@supabase-labs/tanstack-db';
import { queryCollectionOptions } from '@tanstack/query-db-collection';
import { createCollection } from '@tanstack/react-db';
import { z } from 'zod';

import { describeWriteError } from './persist.ts';
import { queryClient } from './query-client.ts';
import { supabase } from './supabase.ts';

/**
 * TanStack DB collections.
 *
 * These live in the admin app rather than `@ctrl/db` on purpose: the adapter is
 * experimental and has no SSR support, and `@ctrl/db` is imported by the API
 * Worker, which must not carry either property.
 *
 * Tenancy: a collection has no tenant filter of its own -- it syncs whatever
 * PostgREST returns, which flat claim-RLS has already narrowed to the accounts
 * in the caller's JWT. For a user who belongs to several accounts, narrow to the
 * active one with a `where` in the live query. RLS is the boundary; the `where`
 * is presentation.
 *
 * Realtime: enabled per collection, and only where cross-session sync earns its
 * cost. The adapter warns that it may consume more Realtime messages than
 * expected, so new collections should start with `realtime: false`.
 */

/**
 * Replace the adapter's mutation handlers.
 *
 * Two problems, both in `@supabase-labs/tanstack-db@0.0.1`:
 *
 *  1. **`onUpdate` sends the whole row.** It PATCHes `{ ...original, ...changes }`,
 *     i.e. every column it fetched with `select=*`. On `products` that includes
 *     `search`, a generated tsvector, which Postgres rejects outright:
 *     `column "search" can only be updated to DEFAULT` (SQLSTATE 428C9).
 *     Sending just the diff fixes that and is better regardless — smaller
 *     payloads, and no clobbering of columns another session just changed.
 *
 *  2. **Every handler loses the error.** The adapter rethrows the PostgREST
 *     error object in a way that reaches the caller as
 *     `new Error("[object Object]")`, so the SQLSTATE and message are gone by
 *     the time anything can report them. An optimistic write then rolls back
 *     with nothing to show the operator but a row that vanished. Each handler
 *     here converts the error into a real message first.
 *
 * Revisit when the adapter handles generated columns and preserves errors.
 */
type UntypedFrom = {
  // biome-ignore lint/suspicious/noExplicitAny: `from()` on the generated Database type needs a literal table name, and this helper is generic
  from(table: string): any;
};

function wrapMutations<T extends object>(
  options: T,
  tableName: string,
  /**
   * The columns identifying a row. Usually `id`, but a join table like
   * `product_tag_assignments` has no surrogate key and is addressed by its
   * pair — filtering on a non-existent `id` there would match nothing and
   * delete nothing, silently.
   */
  keyColumns: string[] = ['id'],
): T {
  const db = supabase as unknown as UntypedFrom;

  // biome-ignore lint/suspicious/noExplicitAny: a PostgREST builder, deliberately untyped here
  const byKey = (query: any, row: Record<string, unknown>) =>
    keyColumns.reduce((acc, column) => acc.eq(column, row[column]), query);

  return {
    ...options,

    // biome-ignore lint/suspicious/noExplicitAny: the adapter's mutation generics are not exported
    onInsert: async ({ transaction, collection }: any) => {
      await Promise.all(
        // biome-ignore lint/suspicious/noExplicitAny: see above
        transaction.mutations.map(async (mutation: any) => {
          const { data, error } = await db
            .from(tableName)
            .insert({ ...mutation.modified })
            .select()
            .single();

          if (error) throw new Error(describeWriteError(error));

          mutation.modified = data;
          collection.utils.writeInsert(data);
        }),
      );

      return { refetch: false };
    },

    // biome-ignore lint/suspicious/noExplicitAny: see above
    onUpdate: async ({ transaction, collection }: any) => {
      await Promise.all(
        // biome-ignore lint/suspicious/noExplicitAny: see above
        transaction.mutations.map(async (mutation: any) => {
          const { original, changes } = mutation;

          const { data, error } = await byKey(db.from(tableName).update(changes), original)
            .select()
            .single();

          if (error) throw new Error(describeWriteError(error));

          mutation.modified = data;
          collection.utils.writeUpdate(data);
        }),
      );

      return { refetch: false };
    },

    // biome-ignore lint/suspicious/noExplicitAny: see above
    onDelete: async ({ transaction, collection }: any) => {
      await Promise.all(
        // biome-ignore lint/suspicious/noExplicitAny: see above
        transaction.mutations.map(async (mutation: any) => {
          const { error } = await byKey(db.from(tableName).delete(), mutation.original);

          if (error) throw new Error(describeWriteError(error));

          collection.utils.writeDelete(mutation.key);
        }),
      );

      return { refetch: false };
    },
  };
}

const productSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  /*
   * Optional, not merely nullable: the list sync does not fetch it. A
   * product's description is TipTap HTML and, across the seeded catalog, it
   * is the whole weight of the table -- see `productsCollection`.
   */
  description: z.string().nullable().optional(),
  status: z.enum(['draft', 'active', 'archived']),
  strain_type: z.string(),
  thc_percent: z.number().nullable(),
  cbd_percent: z.number().nullable(),
  brand_id: z.string().nullable(),
  category_id: z.string().nullable(),
  strain_id: z.string().nullable(),
  is_cannabis: z.boolean().nullable(),
  thc_mg_per_unit: z.number().nullable(),
  net_weight_grams: z.number().nullable(),
  lab_results_url: z.string().nullable().optional(),
  image_urls: z.array(z.string()),
  // Optional, not merely nullable: the database defaults it, so an insert from
  // the form must not be forced to invent a timestamp.
  updated_at: z.string().nullable().optional(),
});

export type ProductRecord = z.infer<typeof productSchema>;

/**
 * The columns the console actually renders in a list.
 *
 * Everything except `description` and `lab_results_url`. Those two are per
 * product a paragraph of TipTap HTML and a long URL, and across the seeded
 * catalog they are almost the entire table: `select=*` pulls 3.48 MB where
 * this list pulls about 175 KB. The editor fetches the full row for the one
 * product it is editing.
 */
const PRODUCT_LIST_COLUMNS = [
  'id',
  'account_id',
  'name',
  'slug',
  'status',
  'strain_type',
  'thc_percent',
  'cbd_percent',
  'brand_id',
  'category_id',
  'strain_id',
  'is_cannabis',
  'thc_mg_per_unit',
  'net_weight_grams',
  'image_urls',
  'updated_at',
].join(', ');

/**
 * A Standard Schema that only carries a type.
 *
 * `queryCollectionOptions` needs a schema to type the rows a live query
 * yields -- without one every `useLiveQuery` over this collection returns
 * an untyped bag. It cannot be the zod schema: this package bundles its own
 * copy of the Standard Schema spec, and a zod v4 object does not satisfy
 * that copy, so the call silently falls through to the schema-less overload
 * and stops type-checking altogether.
 *
 * Validation is not lost where it matters. The rows come from an explicit
 * column list, and every write is validated by the form's own zod schema
 * before it reaches the collection.
 */
function passthroughSchema<T>() {
  return {
    '~standard': {
      version: 1 as const,
      vendor: 'ctrl',
      validate: (value: unknown) => ({ value: value as T }),
      types: undefined as unknown as { input: T; output: T },
    },
  };
}

/** PostgREST caps a response; the catalog is bigger than one page. */
const PRODUCT_PAGE = 1000;

async function fetchProductList(): Promise<ProductRecord[]> {
  const rows: ProductRecord[] = [];

  for (let from = 0; ; from += PRODUCT_PAGE) {
    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_LIST_COLUMNS)
      .order('id', { ascending: true })
      .range(from, from + PRODUCT_PAGE - 1);

    if (error) throw error;

    rows.push(...((data ?? []) as unknown as ProductRecord[]));

    if (!data || data.length < PRODUCT_PAGE) break;
  }

  return rows;
}

/**
 * Products.
 *
 * Built on `queryCollectionOptions` rather than the Supabase adapter,
 * because the adapter always issues `select=*` and has no option for a
 * column list. That is the whole reason this one collection is different.
 *
 * Realtime is still on -- two staff editing the catalog at once is routine
 * and stale rows cause real mistakes (double price edits, resurrected
 * drafts) -- but a change notification refetches the list rather than
 * applying the payload: the payload is a whole row, including the
 * description this collection deliberately does not carry.
 */
export const productsCollection = createCollection(
  wrapMutations(
    queryCollectionOptions({
      queryClient,
      queryKey: ['products-collection'],
      schema: passthroughSchema<ProductRecord>(),
      queryFn: () => fetchProductList(),
      getKey: (product) => product.id,
      // The list is re-fetched on a realtime signal, not on a timer or a
      // window focus: a catalog of two thousand rows is not free.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
    'products',
  ),
);

supabase
  .channel('products-collection')
  .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
    productsCollection.utils.refetch();
  })
  .subscribe();

const categorySchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  parent_id: z.string().nullable(),
  regulatory_category: z.string().nullable(),
  position: z.number().nullable(),
  active: z.boolean().nullable(),
});

export type CategoryRecord = z.infer<typeof categorySchema>;

/** Categories change rarely, so this one does not need a Realtime channel. */
export const categoriesCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'categories',
      schema: categorySchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'categories',
  ),
);

/**
 * The rest of the catalog.
 *
 * These are the bounded, frequently-filtered lookups the editor and catalog
 * screens join against, so syncing them whole is the point: filtering happens
 * in memory with no round-trip. They change rarely, so none needs a Realtime
 * channel — the adapter warns it can consume more messages than expected, and
 * a stale brand name is not worth a socket.
 *
 * Deliberately absent: customers and orders. Those are PII-bearing and grow
 * without bound, so they keep the loader's server-side filtering and paging —
 * see `customerQueries` in @ctrl/db and ./customers.ts.
 */

const brandSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  active: z.boolean().nullable(),
});

export type BrandRecord = z.infer<typeof brandSchema>;

export const brandsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'brands',
      schema: brandSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'brands',
  ),
);

/**
 * Variants, for the price a product lists at.
 *
 * One row per product in this catalog, and only the columns a list needs -- a
 * variant carries no long text, so syncing them costs a fraction of what the
 * products themselves do.
 */
const variantSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  product_id: z.string(),
  name: z.string().nullable(),
  sku: z.string().nullable(),
  unit: z.string(),
  unit_count: z.number().nullable(),
  // `numeric` on the wire, so PostgREST may send a string to keep precision.
  weight_grams: z.union([z.number(), z.string()]).nullable(),
  price_cents: z.number().nullable(),
  compare_at_price_cents: z.number().nullable(),
  position: z.number().nullable(),
  active: z.boolean().nullable(),
  updated_at: z.string().nullable().optional(),
});

export type VariantRecord = z.infer<typeof variantSchema>;

export const variantsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'product_variants',
      schema: variantSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'product_variants',
  ),
);

/**
 * Collections, and the rules that define them.
 *
 * Every collection in the seeded catalog is rule-based: membership is
 * evaluated from `collection_rules` rather than stored in
 * `collection_products`, which is why a collection can show a rule count but
 * not a member count without running the rules.
 */
const collectionSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  kind: z.string().nullable(),
  position: z.number().nullable(),
  active: z.boolean().nullable(),
  rules_match: z.string().nullable(),
});

export type CollectionRecord = z.infer<typeof collectionSchema>;

export const collectionsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'collections',
      schema: collectionSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'collections',
  ),
);

const collectionRuleSchema = z.object({
  // bigint identity, so PostgREST sends a number rather than a uuid string.
  id: z.number(),
  account_id: z.string(),
  collection_id: z.string(),
  field: z.string(),
  operator: z.string(),
  value: z.unknown().nullable(),
});

export type CollectionRuleRecord = z.infer<typeof collectionRuleSchema>;

export const collectionRulesCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'collection_rules',
      schema: collectionRuleSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'collection_rules',
  ),
);

/** Merchandising tags: the third tab of the taxonomy screen. */
const productTagSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  kind: z.string().nullable(),
});

export type ProductTagRecord = z.infer<typeof productTagSchema>;

export const productTagsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'product_tags',
      schema: productTagSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'product_tags',
  ),
);

/**
 * Stock, per variant per location.
 *
 * Realtime is on: two people counting the same shelf is the case this table
 * exists for, and a stale on-hand number is how stock goes missing.
 */
const inventoryLevelSchema = z.object({
  id: z.number(),
  account_id: z.string(),
  variant_id: z.string(),
  location_id: z.string(),
  // numeric columns; PostgREST sends them as strings to preserve precision.
  on_hand: z.union([z.number(), z.string()]).nullable(),
  reserved: z.union([z.number(), z.string()]).nullable(),
  available: z.union([z.number(), z.string()]).nullable(),
  low_stock_threshold: z.union([z.number(), z.string()]).nullable(),
});

export type InventoryLevelRecord = z.infer<typeof inventoryLevelSchema>;

export const inventoryLevelsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'inventory_levels',
      schema: inventoryLevelSchema,
      keys: ['id'],
      supabase,
      realtime: true,
    }),
    'inventory_levels',
  ),
);

const locationSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  type: z.string().nullable(),
});

export type LocationRecord = z.infer<typeof locationSchema>;

export const locationsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'locations',
      schema: locationSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'locations',
  ),
);

/**
 * Metrc packages: the state's record of physical inventory.
 *
 * Empty in the seeded catalog -- upstream has none -- so the screen's job for
 * now is to say so rather than to look broken.
 */
/**
 * Which tags are on which product.
 *
 * A join table with no surrogate key: its identity is the pair, which is what
 * `keys` names. The adapter joins those into the collection key, so a row is
 * addressed as `"<product>-<tag>"`.
 */
const productTagAssignmentSchema = z.object({
  account_id: z.string(),
  product_id: z.string(),
  tag_id: z.string(),
});

export type ProductTagAssignmentRecord = z.infer<typeof productTagAssignmentSchema>;

export const productTagAssignmentsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'product_tag_assignments',
      schema: productTagAssignmentSchema,
      keys: ['product_id', 'tag_id'],
      supabase,
      realtime: false,
    }),
    'product_tag_assignments',
    ['product_id', 'tag_id'],
  ),
);

const packageSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  metrc_tag: z.string().nullable(),
  product_id: z.string().nullable(),
  variant_id: z.string().nullable(),
  location_id: z.string().nullable(),
  status: z.string().nullable(),
  batch_number: z.string().nullable(),
  quantity: z.union([z.number(), z.string()]).nullable(),
  unit_of_measure: z.string().nullable(),
  expiration_date: z.string().nullable(),
  received_at: z.string().nullable(),
});

export type PackageRecord = z.infer<typeof packageSchema>;

export const packagesCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'packages',
      schema: packageSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'packages',
  ),
);

const strainSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  name: z.string(),
  slug: z.string(),
  // The column is `type`, of type `strain_type`. This said `strain_type`, which
  // no row has, so every sync failed validation -- invisible until something
  // actually read the collection.
  //
  // NOT NULL with a default of 'na', so it is a plain string here: sending an
  // explicit null would override the default and be rejected.
  type: z.string(),
  description: z.string().nullable(),
  lineage: z.string().nullable(),
});

export type StrainRecord = z.infer<typeof strainSchema>;

export const strainsCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'strains',
      schema: strainSchema,
      keys: ['id'],
      supabase,
      realtime: false,
    }),
    'strains',
  ),
);

/**
 * The fleet, live.
 *
 * Bounded (fewer than fifty rows per store) and already in the Realtime
 * publication, so it is the one dispatch table that earns a collection. Live
 * positions arrive here too: `kit.mirror_driver_ping` copies each ping's
 * lat/lng/heading onto `drivers`, so a ping becomes a Realtime UPDATE on this
 * row. The board never reads `driver_location_pings` -- that table is
 * unbounded and the mirror exists precisely so nothing has to.
 */
const driverSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  user_id: z.string().nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  color: z.string().nullable(),
  status: z.string(),
  active: z.boolean(),
  state_badge_number: z.string().nullable(),
  latitude: z.union([z.number(), z.string()]).nullable(),
  longitude: z.union([z.number(), z.string()]).nullable(),
  heading: z.union([z.number(), z.string()]).nullable(),
  speed_mps: z.union([z.number(), z.string()]).nullable(),
  last_ping_at: z.string().nullable(),
  updated_at: z.string().nullable().optional(),
});

export type DriverRecord = z.infer<typeof driverSchema>;

export const driversCollection = createCollection(
  wrapMutations(
    supabaseCollectionOptions({
      tableName: 'drivers',
      schema: driverSchema,
      keys: ['id'],
      supabase,
      realtime: true,
    }),
    'drivers',
  ),
);
